import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { KpiCard } from '@/components/dashboard/kpi-card'
import {
  BrainCircuit,
  ClipboardCheck,
  Send,
  AlertTriangle,
  Coins,
  Settings,
  BookOpen,
  ShieldCheck,
  History,
  ListChecks,
} from 'lucide-react'
import { formatCurrency } from '@roi365/shared'
import { categoryLabels, originLabels } from '@/lib/ai-labels'

interface AiMetrics {
  byStatus: Array<{ status: string; count: number; total_saving: number }>
  byOrigin: Array<{ origin: string; count: number }>
  byCategory: Array<{ category: string; count: number }>
  tokenUsage: {
    total_input: number
    total_output: number
    total_runs: number
    failed_runs: number
    total_cost_eur: number
    runs_without_cost: number
  } | null
  moderationPending: number
}


export function AdminAiDashboardPage() {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin', 'ai', 'metrics'],
    queryFn: () => api.get<AiMetrics>('/admin/ai/metrics'),
  })

  const navLinks = [
    { href: '/admin/ai/tenants', label: 'Configuracion Tenants', icon: Settings, description: 'Gestionar IA por tenant' },
    { href: '/admin/ai/contexts', label: 'Contextos', icon: BookOpen, description: 'Contextos de generacion IA' },
    { href: '/admin/ai/moderation', label: 'Moderacion', icon: ShieldCheck, description: 'Cola de aprobacion' },
    { href: '/admin/ai/runs', label: 'Historial Ejecuciones', icon: History, description: 'Logs de ejecucion IA' },
    { href: '/admin/ai/recommendations', label: 'Recomendaciones', icon: ListChecks, description: 'Vista global de recomendaciones' },
  ]

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="IA - Panel de Control" description="Metricas y accesos rapidos del investigador IA" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg border" style={{ backgroundColor: 'var(--color-gray-50)', borderColor: 'var(--color-border)' }} />
          ))}
        </div>
      </div>
    )
  }

  // Derived totals
  const byStatus = metrics?.byStatus ?? []
  const byOrigin = metrics?.byOrigin ?? []
  const byCategory = metrics?.byCategory ?? []
  const tokenUsage = metrics?.tokenUsage ?? { total_input: 0, total_output: 0, total_runs: 0, failed_runs: 0, total_cost_eur: 0, runs_without_cost: 0 }

  const total = byStatus.reduce((acc, s) => acc + (s.count ?? 0), 0)
  const pendingReview = byStatus.find((s) => s.status === 'pending_review')?.count ?? 0
  const published = byStatus.find((s) => s.status === 'published')?.count ?? 0
  const errorRate = tokenUsage.total_runs > 0 ? (tokenUsage.failed_runs / tokenUsage.total_runs) * 100 : 0


  return (
    <div className="space-y-6">
      <PageHeader title="IA - Panel de Control" description="Metricas y accesos rapidos del motor de recomendaciones IA" />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Total Recomendaciones" value={total} icon={BrainCircuit} accentColor="var(--color-brand-primary)" />
        <KpiCard label="Pendientes Revision" value={metrics?.moderationPending ?? pendingReview} icon={ClipboardCheck} accentColor="var(--color-warning)" />
        <KpiCard label="Publicadas" value={published} icon={Send} accentColor="var(--color-success)" />
        <KpiCard
          label="Tasa de Error"
          value={`${errorRate.toFixed(1)}%`}
          icon={AlertTriangle}
          accentColor={errorRate > 10 ? 'var(--color-error)' : 'var(--color-success)'}
        />
      </div>

      {/* Token Usage & Breakdowns */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Token Usage */}
        <Card>
          <CardHeader>
            <CardTitle>Uso de Tokens</CardTitle>
            <Coins className="h-5 w-5" style={{ color: 'var(--color-text-tertiary)' }} />
          </CardHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Tokens entrada</span>
              <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {tokenUsage.total_input.toLocaleString('es-ES')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Tokens salida</span>
              <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {tokenUsage.total_output.toLocaleString('es-ES')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Ejecuciones totales</span>
              <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {tokenUsage.total_runs.toLocaleString('es-ES')}
              </span>
            </div>
            <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Coste registrado</span>
              <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--color-brand-primary)' }}>
                {formatCurrency(tokenUsage.total_cost_eur)}{tokenUsage.runs_without_cost > 0 ? ` (+${tokenUsage.runs_without_cost} sin precio)` : ''}
              </span>
            </div>
          </div>
        </Card>

        {/* By Origin */}
        <Card>
          <CardHeader>
            <CardTitle>Por Origen</CardTitle>
          </CardHeader>
          <div className="space-y-3">
            {byOrigin.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>Sin datos</p>
            ) : (
              byOrigin.map((item) => (
                <div key={item.origin} className="flex items-center justify-between">
                  <Badge variant={item.origin === 'rule' ? 'neutral' : item.origin === 'ai_enriched' ? 'info' : 'premium'}>
                    {originLabels[item.origin] ?? item.origin}
                  </Badge>
                  <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                    {item.count.toLocaleString('es-ES')}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* By Category */}
        <Card>
          <CardHeader>
            <CardTitle>Por Categoria</CardTitle>
          </CardHeader>
          <div className="space-y-3">
            {byCategory.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>Sin datos</p>
            ) : (
              byCategory.map((item) => (
                <div key={item.category} className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {categoryLabels[item.category] ?? item.category}
                  </span>
                  <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                    {item.count.toLocaleString('es-ES')}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Quick Navigation */}
      <Card>
        <CardHeader>
          <CardTitle>Accesos Rapidos</CardTitle>
        </CardHeader>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex items-center gap-3 rounded-lg border p-4 transition-colors"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-50)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
            >
              <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--color-brand-primary-50)' }}>
                <link.icon className="h-5 w-5" style={{ color: 'var(--color-brand-primary)' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{link.label}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{link.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  )
}
